/**
 * i18n-review core types.
 *
 * Framework-light, dependency-light. NOTHING in `core/` may import jobcrawls
 * code, AWS SDKs, or app modules — that boundary is what makes the module
 * extractable/open-sourceable (see TEC-465 P4). Concrete adapters live in
 * `../adapters` and stay in the host repo.
 */

/** The reference locale. Its values are the source of truth translations derive from. */
export const SOURCE_LOCALE = 'en' as const;
export type SourceLocale = typeof SOURCE_LOCALE;

/** Locales a reviewer can correct. The design must not preclude adding more. */
export const REVIEW_LOCALES = ['fi', 'sv'] as const;
export type ReviewLocale = (typeof REVIEW_LOCALES)[number];

export function isReviewLocale(value: unknown): value is ReviewLocale {
  return typeof value === 'string' && (REVIEW_LOCALES as readonly string[]).includes(value);
}

/**
 * Per (id, locale) status.
 * - `untranslated`: no value exists for that (id, locale).
 * - `machine`: value was produced by the auto-translation pipeline.
 * - `human_reviewed`: a reviewer saved a correction. The ONLY state the
 *   pipeline must never overwrite. This single rule is the entire contract
 *   between this feature and the translation pipeline.
 */
export type ReviewStatus = 'untranslated' | 'machine' | 'human_reviewed';

/** Statuses that correspond to a stored override row (a value exists). */
export type StoredStatus = Exclude<ReviewStatus, 'untranslated'>;

/** One row in the override store, one per (id, locale). */
export interface OverrideRecord {
  /** react-intl message ID. */
  id: string;
  locale: ReviewLocale;
  /** The correction (ICU message string). */
  value: string;
  status: StoredStatus;
  /** Reviewer identity (e.g. email/user id), null for machine writes. */
  reviewedBy: string | null;
  /** ISO-8601 timestamp of the last write. Doubles as the audit trail. */
  updatedAt: string;
}

/** Write payload for the override store; the store stamps `updatedAt`. */
export interface OverrideWriteInput {
  id: string;
  locale: ReviewLocale;
  value: string;
  status: StoredStatus;
  reviewedBy: string | null;
}

/** What the message-source adapter exposes for a single message ID. */
export interface MessageSourceEntry {
  id: string;
  /** EN (source) value — the reference a correction's ICU args must match. */
  source: string;
  /** FormatJS `description` for reviewer context, where present. */
  description?: string;
  /**
   * The machine-translated value per review locale, or null when the locale
   * has no machine translation yet (→ untranslated unless an override exists).
   */
  machine: Record<ReviewLocale, string | null>;
}

/** Where a message ID is rendered, per the runtime usage index (P2). */
export interface UsageEntry {
  /** Route path the ID was rendered on (e.g. "/fi/jobs/..."). */
  route: string;
  /** Optional component name if instrumentation can attribute it. */
  component?: string;
}

/** A fully-merged catalogue row: machine source + live overrides + usage. */
export interface CatalogueEntry {
  id: string;
  description?: string;
  source: string;
  locales: Record<ReviewLocale, CatalogueLocaleCell>;
  usage: UsageEntry[];
}

export interface CatalogueLocaleCell {
  /** Effective value shown/used: override value when present, else machine. */
  value: string;
  /** The machine value (for "revert" / comparison), or null if none. */
  machineValue: string | null;
  status: ReviewStatus;
  reviewedBy?: string | null;
  updatedAt?: string | null;
}

/** Filters the catalogue list endpoint understands. */
export interface CatalogueFilter {
  locale?: ReviewLocale;
  status?: ReviewStatus;
  /** Free-text across id, description, and values. */
  search?: string;
  /** Only rows updated at/after this ISO timestamp. */
  modifiedSince?: string;
}
